import React, { useState, useEffect } from 'react';
import Input from './Input';

export interface ColorPickerProps {
  value?: string;
  onChange?: (color: string) => void;
  presets?: string[];
  error?: string;
  className?: string;
  showContrastChecker?: boolean;
  contrastAgainst?: string;
}

const DEFAULT_PRESETS = [
  '#2563eb', // Blue
  '#dc2626', // Red
  '#16a34a', // Green
  '#f59e0b', // Amber
  '#8b5cf6', // Purple
  '#ec4899', // Pink
  '#06b6d4', // Cyan
  '#64748b', // Slate
  '#000000', // Black
  '#ffffff', // White
];

function getLuminance(hex: string): number {
  const rgb = hexToRgb(hex);
  if (!rgb) return 0;

  const [r, g, b] = [rgb.r, rgb.g, rgb.b].map((c) => {
    const sRGB = c / 255;
    return sRGB <= 0.03928 ? sRGB / 12.92 : Math.pow((sRGB + 0.055) / 1.055, 2.4);
  });

  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
}

function getContrastRatio(color1: string, color2: string): number {
  const lum1 = getLuminance(color1);
  const lum2 = getLuminance(color2);
  const brightest = Math.max(lum1, lum2);
  const darkest = Math.min(lum1, lum2);
  return (brightest + 0.05) / (darkest + 0.05);
}

function getWCAGRating(ratio: number): { level: string; color: string } {
  if (ratio >= 7) return { level: 'AAA', color: 'var(--color-success)' };
  if (ratio >= 4.5) return { level: 'AA', color: 'var(--color-success)' };
  if (ratio >= 3) return { level: 'AA Large', color: 'var(--color-accent)' };
  return { level: 'Fail', color: 'var(--color-error)' };
}

export const ColorPicker: React.FC<ColorPickerProps> = ({
  value = '#2563eb',
  onChange,
  presets = DEFAULT_PRESETS,
  error,
  className = '',
  showContrastChecker = false,
  contrastAgainst = '#ffffff',
}) => {
  const [hexValue, setHexValue] = useState(value);

  useEffect(() => {
    setHexValue(value);
  }, [value]);

  const handleColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newColor = e.target.value;
    setHexValue(newColor);
    onChange?.(newColor);
  };

  const handleHexInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    let newValue = e.target.value;

    if (!newValue.startsWith('#')) {
      newValue = '#' + newValue;
    }

    setHexValue(newValue);

    if (/^#[0-9A-Fa-f]{6}$/.test(newValue)) {
      onChange?.(newValue);
    }
  };

  const handlePresetClick = (preset: string) => {
    setHexValue(preset);
    onChange?.(preset);
  };

  const contrastRatio = showContrastChecker ? getContrastRatio(value, contrastAgainst) : 0;
  const wcagRating = showContrastChecker ? getWCAGRating(contrastRatio) : null;

  return (
    <div className={`color-picker-wrapper ${className}`}>
      <div className="color-picker-input-wrapper">
        <input
          type="color"
          value={value}
          onChange={handleColorChange}
          className="color-picker-native"
          aria-label="Color picker"
        />
        <div className="color-picker-hex" style={{ flex: 1 }}>
          <Input
            type="text"
            value={hexValue}
            onChange={handleHexInput}
            placeholder="#000000"
            maxLength={7}
            error={error}
            style={{ textTransform: 'uppercase', fontFamily: 'var(--font-mono)' }}
          />
        </div>
      </div>

      {showContrastChecker && wcagRating && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            fontSize: 'var(--text-sm)',
            fontFamily: 'var(--font-mono)',
          }}
        >
          <span style={{ color: 'var(--color-muted)' }}>Contrast:</span>
          <span>{contrastRatio.toFixed(2)}:1</span>
          <span
            style={{
              backgroundColor: wcagRating.color,
              color: 'var(--color-bg)',
              padding: '2px 6px',
              fontWeight: 600,
              fontSize: 'var(--text-xs)',
            }}
          >
            {wcagRating.level}
          </span>
        </div>
      )}

      <div className="color-picker-presets">
        {presets.map((preset) => (
          <button
            key={preset}
            type="button"
            className={`color-picker-preset ${preset === value ? 'color-picker-preset-selected' : ''}`}
            style={{ backgroundColor: preset }}
            onClick={() => handlePresetClick(preset)}
            aria-label={`Select color ${preset}`}
            title={preset}
          />
        ))}
      </div>
    </div>
  );
};

export default ColorPicker;
